import React, { useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
} from '@mui/material';
import InputField from '../molecules/InputField';
import Button from '../atoms/Button';
import { UserService } from '../../services/user/UserService';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Visibility } from '@mui/icons-material';
import { PatternFormat } from 'react-number-format';
import styles from '../../pages/login/Login.module.css';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin }) => {
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [showPassword, setShowPassword] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [cpf, setCPF] = useState('');
  const [phone, setPhone] = useState('');
  const [cnpj, setCNPJ] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [type, setType] = useState('CUSTOMER');
  const [passwordHelperText, setPasswordHelperText] = useState('');
  const [confirmHelperText, setConfirmHelperText] = useState('');
  const minPasswordLength = 8;
  const passwordHasMinLength = password.length >= minPasswordLength;
  const passwordHasLetter = /[A-Za-z]/.test(password);
  const passwordHasNumber = /\d/.test(password);
  const isPasswordComplex = passwordHasMinLength && passwordHasLetter && passwordHasNumber;
  const isConfirmValid = password === confirmPassword;
  const passwordStatusText =
    password.trim().length > 0
      ? isPasswordComplex
        ? 'Senha atende aos requisitos'
        : 'Use no mínimo 8 caracteres com letras e números'
      : '';
  const confirmStatusText =
    confirmPassword.trim().length > 0
      ? isConfirmValid
        ? 'Senhas conferem'
        : 'As senhas não conferem'
      : '';
  const isSubmitDisabled =
    !acceptTerms ||
    !email.trim() ||
    !password.trim() ||
    !confirmPassword.trim() ||
    !isPasswordComplex ||
    !isConfirmValid;

  const handleCreate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    try {
      await UserService.create(name, password, email, type, cpf, cnpj, phone);
      setSucessAlert(true);
      setSucessAlertMessage('Cadastrado com Sucesso!');
      onSwitchToLogin();
    } catch (err: any) {
      console.log(err);
      const serviceMessage = err?.data?.detail || 'Erro ao cadastrar. Tente novamente.';
      setErrorAlertMessage(serviceMessage);
      setErrorAlert(true);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCNPJ('');
    setCPF('');
    setType(event.target.value);
  };

  const validate = () => {
    const newErrors: Record<string, boolean> = {};
    setPasswordHelperText('');
    setConfirmHelperText('');

    if (!email.trim()) newErrors.email = true;
    if (!password.trim()) newErrors.password = true;
    if (!confirmPassword.trim()) newErrors.confirmPassword = true;
    if (!acceptTerms) newErrors.acceptTerms = true;

    if (password.trim() && !isPasswordComplex) {
      newErrors.password = true;
      setPasswordHelperText('Mínimo 8 caracteres, com letras e números');
    }

    if (password && confirmPassword && password !== confirmPassword) {
      newErrors.confirmPassword = true;
      setConfirmHelperText('As senhas não conferem');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [successAlert, setSucessAlert] = useState(false);
  const [sucessAlertMessage, setSucessAlertMessage] = useState('');
  const [errorAlert, setErrorAlert] = useState(false);
  const [errorAlertMessage, setErrorAlertMessage] = useState('');
  const handleCloseSuccessAlert = () => {
    setSucessAlert(false);
    onSwitchToLogin();
  };
  const handleCloseErrorAlert = () => {
    setErrorAlert(false);
  };

  const [helperText, setHelperText] = useState('');
  const handlerEmail = (value: string) => {
    const newErrors: Record<string, boolean> = { ...errors };

    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(value)) {
      newErrors.email = true;
      setHelperText('Informe um e-mail válido');
    } else {
      newErrors.email = false;
      setHelperText('');
    }
    setEmail(value);
    setErrors(newErrors);
  };

  return (
    <Box>
      <Snackbar open={successAlert} autoHideDuration={6000} onClose={handleCloseSuccessAlert}>
        <Alert
          onClose={handleCloseSuccessAlert}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {sucessAlertMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={errorAlert} autoHideDuration={6000} onClose={handleCloseErrorAlert}>
        <Alert
          onClose={handleCloseErrorAlert}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {errorAlertMessage}
        </Alert>
      </Snackbar>
      <form onSubmit={handleCreate}>
        <Box className={styles.formStack}>
          <FormControl fullWidth>
            <TextField
              required
              variant="outlined"
              label="Endereço de e-mail"
              type="email"
              value={email}
              error={!!errors.email}
              helperText={helperText}
              onChange={(e) => handlerEmail(e.target.value)}
            />
          </FormControl>

          <FormControl fullWidth>
            <PatternFormat
              customInput={TextField}
              label="Whatsapp"
              variant="outlined"
              fullWidth
              format="(##) #####-####"
              mask="_"
              value={phone}
              onValueChange={(values) => setPhone(values.value)}
            />
          </FormControl>

          <Box className={styles.row}>
            <TextField
              fullWidth
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              value={password}
              error={errors.password}
              helperText={passwordHelperText || passwordStatusText}
              variant="outlined"
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseUp={(e) => e.preventDefault()}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirmar Senha"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              error={errors.confirmPassword}
              helperText={confirmHelperText || confirmStatusText}
              variant="outlined"
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseUp={(e) => e.preventDefault()}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <FormControlLabel
            className={styles.termsRow}
            control={
              <Checkbox
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                size="small"
              />
            }
            label={
              <span>
                Eu aceito os{' '}
                <a
                  href="https://umespaco.com/como-funciona/"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#1f75cf', fontWeight: 600, textDecoration: 'none' }}
                >
                  Termos de Serviço e a Política de Privacidade
                </a>
              </span>
            }
          />

          <FormControl fullWidth>
            <Button
              type="submit"
              variant="contained"
              label="CADASTRAR"
              onClick={handleCreate}
              disabled={isSubmitDisabled}
              sx={{
                backgroundColor: '#1f75cf',
                color: '#ffffff',
                fontWeight: 600,
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(31, 117, 207, 0.2)',
                '&:hover': {
                  backgroundColor: '#1a67b6',
                },
              }}
            />
          </FormControl>
        </Box>
      </form>
    </Box>
  );
};

export default RegisterForm;
